import React, { useState, useRef, useCallback, useEffect, useImperativeHandle, forwardRef } from 'react';
import { logger } from '@/lib/logger';
import { useFloorPlanMetadata } from '@/hooks/useFloorPlanMetadata';
import { useAppStore } from '@/stores/appStore';

export interface FloorPlanEditWrapperRef {
  undo: () => void;
  reset: () => void;
  cancel: () => void;
  save: () => Promise<void>;
}

interface FloorPlanEditWrapperProps {
  floorPlanId: string;
  isActive: boolean;
  children: React.ReactNode;
  imageSize: { width: number; height: number };
  viewport: { scale: number; positionX: number; positionY: number };
  onStateChange?: (state: { position: { x: number; y: number }; size: { width: number; height: number } }) => void;
}

export const FloorPlanEditWrapper = forwardRef<FloorPlanEditWrapperRef, FloorPlanEditWrapperProps>(({ 
  floorPlanId, 
  isActive, 
  children, 
  imageSize,
  viewport,
  onStateChange 
}, ref) => {
  const { floorPlanMetadata, saveFloorPlanMetadata } = useFloorPlanMetadata(floorPlanId);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialPosition, setInitialPosition] = useState({ x: 0, y: 0 });
  const [initialSize, setInitialSize] = useState({ width: 0, height: 0 });
  const [tempPosition, setTempPosition] = useState({ x: 0, y: 0 });
  const [tempSize, setTempSize] = useState({ width: 0, height: 0 });
  const [initialState, setInitialState] = useState<{ position: { x: number; y: number }; size: { width: number; height: number } } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Initialize temp values from metadata
  useEffect(() => {
    if (floorPlanMetadata) {
      const position = {
        x: floorPlanMetadata.position_x || 0,
        y: floorPlanMetadata.position_y || 0
      };
      const size = {
        width: floorPlanMetadata.image_width || imageSize.width,
        height: floorPlanMetadata.image_height || imageSize.height
      };
      
      setTempPosition(position);
      setTempSize(size);
      
      // Capture initial state when entering edit mode
      if (isActive && !initialState) {
        setInitialState({ position, size });
      }
      
      // Clear initial state when exiting edit mode
      if (!isActive && initialState) {
        setInitialState(null);
      }
    }
  }, [floorPlanMetadata, imageSize, isActive, initialState]);
  
  // Calculate current values
  const currentWidth = floorPlanMetadata ? (tempSize.width || floorPlanMetadata.image_width || imageSize.width) : imageSize.width;
  const currentHeight = floorPlanMetadata ? (tempSize.height || floorPlanMetadata.image_height || imageSize.height) : imageSize.height;
  const currentX = floorPlanMetadata ? (tempPosition.x ?? floorPlanMetadata.position_x ?? 0) : 0;
  const currentY = floorPlanMetadata ? (tempPosition.y ?? floorPlanMetadata.position_y ?? 0) : 0;
  
  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    undo: () => {
      if (initialState) {
        setTempPosition(initialState.position);
        setTempSize(initialState.size);
      }
    },
    reset: () => {
      // Reset to initial state when edit mode was entered
      if (initialState) {
        setTempPosition(initialState.position);
        setTempSize(initialState.size);
      } else {
        // Fallback to saved metadata if no initial state
        if (floorPlanMetadata) {
          setTempPosition({
            x: floorPlanMetadata.position_x || 0,
            y: floorPlanMetadata.position_y || 0
          });
          setTempSize({
            width: floorPlanMetadata.image_width || imageSize.width,
            height: floorPlanMetadata.image_height || imageSize.height
          });
        }
      }
    },
    cancel: () => {
      // Revert to saved metadata without saving
      if (floorPlanMetadata) {
        setTempPosition({
          x: floorPlanMetadata.position_x || 0,
          y: floorPlanMetadata.position_y || 0
        });
        setTempSize({
          width: floorPlanMetadata.image_width || imageSize.width,
          height: floorPlanMetadata.image_height || imageSize.height
        });
      }
    },
    save: async () => {
      // Save the current temp position and size
      try {
        await saveFloorPlanMetadata({
          position_x: Math.round(tempPosition.x),
          position_y: Math.round(tempPosition.y),
          image_width: Math.round(tempSize.width),
          image_height: Math.round(tempSize.height)
        });
        // Update initial state to the saved values
        setInitialState({
          position: tempPosition,
          size: tempSize
        });
      } catch (error) {
        logger.error('Error saving floor plan metadata:', error);
        throw error;
      }
    }
  }), [initialState, floorPlanMetadata, imageSize, tempPosition, tempSize, saveFloorPlanMetadata]);
  
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0 || !isActive) return;
    
    // Check if we clicked on a resize handle
    const target = e.target as HTMLElement;
    if (target.classList.contains('resize-handle')) return;
    
    // Check if spacebar is pressed (for panning) or pan tool is active
    const { ui } = useAppStore.getState();
    const spacePressed = ui.toolbar.spacePressed;
    const activeTool = ui.toolbar.activeTool;
    if (spacePressed || activeTool === 'pan') return;
    
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const x = (e.clientX - rect.left) / viewport.scale;
    const y = (e.clientY - rect.top) / viewport.scale;
    
    setIsDragging(true);
    setDragStart({ x, y });
    setInitialPosition({ x: currentX, y: currentY });
    e.preventDefault();
  }, [currentX, currentY, viewport, isActive]);
  
  const handleResizeStart = useCallback((e: React.MouseEvent, handle: string) => {
    if (e.button !== 0 || !isActive) return;
    
    e.stopPropagation();
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const x = (e.clientX - rect.left) / viewport.scale;
    const y = (e.clientY - rect.top) / viewport.scale;
    
    setIsResizing(true);
    setResizeHandle(handle);
    setDragStart({ x, y });
    setInitialPosition({ x: currentX, y: currentY });
    setInitialSize({ width: currentWidth, height: currentHeight });
    e.preventDefault();
  }, [currentX, currentY, currentWidth, currentHeight, viewport, isActive]);
  
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging && !isResizing) return;
    
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const x = (e.clientX - rect.left) / viewport.scale;
    const y = (e.clientY - rect.top) / viewport.scale;
    
    if (isDragging) {
      const deltaX = x - dragStart.x;
      const deltaY = y - dragStart.y;
      
      setTempPosition({
        x: initialPosition.x + deltaX,
        y: initialPosition.y + deltaY
      });
    } else if (isResizing && resizeHandle) {
      const deltaX = x - dragStart.x;
      const deltaY = y - dragStart.y;
      
      let newWidth = initialSize.width;
      let newHeight = initialSize.height;
      let newX = initialPosition.x;
      let newY = initialPosition.y;
      
      // Calculate new dimensions based on resize handle
      if (resizeHandle.includes('right')) {
        newWidth = Math.max(100, initialSize.width + deltaX);
      }
      if (resizeHandle.includes('left')) {
        const widthDelta = Math.min(deltaX, initialSize.width - 100);
        newWidth = initialSize.width - widthDelta;
        newX = initialPosition.x + widthDelta;
      }
      if (resizeHandle.includes('bottom')) {
        newHeight = Math.max(100, initialSize.height + deltaY);
      }
      if (resizeHandle.includes('top')) {
        const heightDelta = Math.min(deltaY, initialSize.height - 100);
        newHeight = initialSize.height - heightDelta;
        newY = initialPosition.y + heightDelta;
      }
      
      // Always maintain aspect ratio
      const aspectRatio = initialSize.width / initialSize.height;
      if (resizeHandle === 'left' || resizeHandle === 'right') {
        newHeight = newWidth / aspectRatio;
      } else if (resizeHandle === 'top' || resizeHandle === 'bottom') {
        newWidth = newHeight * aspectRatio;
      } else {
        // Corner handles - use the larger change
        const widthChange = Math.abs(newWidth - initialSize.width);
        const heightChange = Math.abs(newHeight - initialSize.height);
        if (widthChange > heightChange) {
          newHeight = newWidth / aspectRatio;
        } else {
          newWidth = newHeight * aspectRatio;
        }
      }
      
      setTempSize({ width: newWidth, height: newHeight });
      setTempPosition({ x: newX, y: newY });
    }
  }, [isDragging, isResizing, resizeHandle, dragStart, initialPosition, initialSize, viewport]);
  
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeHandle(null);
  }, []);
  
  useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);
  
  // Always wrap the children, but only show edit controls when active
  return (
    <div 
      ref={containerRef}
      className="floor-plan-edit-wrapper absolute inset-0"
      style={{
        cursor: isActive && isDragging ? 'grabbing' : 'default',
      }}
    >
      <div
        className="absolute"
        style={{
          left: currentX,
          top: currentY,
          width: currentWidth,
          height: currentHeight,
          border: isActive ? '2px solid #3B82F6' : 'none',
          boxShadow: isActive ? '0 0 0 1px rgba(59, 130, 246, 0.3)' : 'none',
          cursor: isActive ? (isDragging ? 'grabbing' : 'grab') : 'default',
        }}
        onMouseDown={isActive ? handleMouseDown : undefined}
      >
        {children}
        
        {/* Resize handles - only show when active */}
        {isActive && ['top-left', 'top-right', 'bottom-left', 'bottom-right', 'top', 'right', 'bottom', 'left'].map((handle) => (
          <div
            key={handle}
            className={`resize-handle absolute w-4 h-4 bg-blue-500 border-2 border-white rounded-full shadow-md hover:bg-blue-600 transition-colors ${
              handle.includes('top') && handle.includes('left') ? 'cursor-nw-resize' :
              handle.includes('top') && handle.includes('right') ? 'cursor-ne-resize' :
              handle.includes('bottom') && handle.includes('left') ? 'cursor-sw-resize' :
              handle.includes('bottom') && handle.includes('right') ? 'cursor-se-resize' :
              handle === 'top' || handle === 'bottom' ? 'cursor-ns-resize' :
              'cursor-ew-resize'
            }`}
            style={{
              ...getHandlePosition(handle),
              transform: 'translate(-50%, -50%)',
              zIndex: 10,
              // Scale handles based on viewport zoom to maintain visibility
              // Base size is 16px, scales up when zoomed out
              width: `${Math.max(16, Math.min(24, 20 / viewport.scale))}px`,
              height: `${Math.max(16, Math.min(24, 20 / viewport.scale))}px`,
            }}
            onMouseDown={(e) => handleResizeStart(e, handle)}
          />
        ))}
        
        {/* Info overlay - only show when active */}
        {isActive && (
          <div className="absolute -top-8 left-0 bg-black/70 text-white px-2 py-1 rounded text-xs pointer-events-none whitespace-nowrap">
            Position: ({Math.round(currentX)}, {Math.round(currentY)}) | Size: {Math.round(currentWidth)} × {Math.round(currentHeight)}px
          </div>
        )}
      </div>
    </div>
  );
});

FloorPlanEditWrapper.displayName = 'FloorPlanEditWrapper';

function getHandlePosition(handle: string) {
  switch (handle) {
    case 'top-left': return { top: 0, left: 0 };
    case 'top-right': return { top: 0, right: 0 };
    case 'bottom-left': return { bottom: 0, left: 0 };
    case 'bottom-right': return { bottom: 0, right: 0 };
    case 'top': return { top: 0, left: '50%' };
    case 'right': return { top: '50%', right: 0 };
    case 'bottom': return { bottom: 0, left: '50%' };
    case 'left': return { top: '50%', left: 0 };
    default: return {};
  }
}
